import { forwardRef } from 'react';
import { makeStyles } from '@material-ui/core';

import HomeDeliveryNoteCard from '../Cards/HomeDeliveryNoteCard.component';
import DeliveryPointDeliveryNoteCard from '../Cards/DeliveryPointDeliveryNoteCard.component';
import EcoDeliveryNoteCard from '../Cards/EcoDeliveryNoteCard.component';
import pluralFormatter from '../../../shared/utils/plural-formater';
import { groupDeliveryNotesByType } from '../../utils/group-delivery-notes-by-type.util';
import { DeliveryNote, DeliveryNoteTypes } from '../../types/delivery-note.type';

const useStyles = makeStyles({
  hiddenWrapper: {
    position: 'absolute',
    overflow: 'hidden',
    height: 0,
    width: 0,
  },

  document: {
    color: 'black',
    maxWidth: '95%',
    margin: '0 auto',
  },

  sectionBreak: {
    pageBreakBefore: 'always',
  },

  noteWrapper: {
    pageBreakInside: 'avoid',
    pageBreakAfter: 'always',
    position: 'relative',
    minHeight: '97vh',
    paddingTop: '12px',
    paddingBottom: '2.5rem',
    boxSizing: 'border-box',

    '& .MuiChip-root': {
      backgroundColor: '#e0e0e0',
      color: 'black',
    },
  },

  pageFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    fontSize: '12px',
  },
});

const CARD_COMPONENT_BY_TYPE = {
  [DeliveryNoteTypes.HOME]: HomeDeliveryNoteCard,
  [DeliveryNoteTypes.DELIVERY_POINT]: DeliveryPointDeliveryNoteCard,
  [DeliveryNoteTypes.ECO]: EcoDeliveryNoteCard,
} as const;

type PrintSection = {
  id: string;
  title: string;
  notes: DeliveryNote[];
};

function formatSectionTitle(section: PrintSection): string {
  const orderCount = section.notes.reduce((count, note) => count + note.orders.length, 0);
  const orderCountLabel = pluralFormatter({
    count: orderCount,
    singular: `${orderCount} commande`,
    plural: `${orderCount} commandes`,
  });

  return `${section.title} (${orderCountLabel})`;
}

interface PrintDeliveryNotesContainerProps {
  deliveryNotes: DeliveryNote[] | null;
}

export const PrintDeliveryNotesContainer = forwardRef<HTMLDivElement, PrintDeliveryNotesContainerProps>(
  function PrintDeliveryNotesContainer({ deliveryNotes }, ref) {
    const classes = useStyles();

    if (!deliveryNotes || deliveryNotes.length === 0) return null;

    const { home, deliveryPoint, eco } = groupDeliveryNotesByType(deliveryNotes);

    const sections: PrintSection[] = [
      { id: 'home', title: 'Livraisons à domicile', notes: home },
      { id: 'delivery-point', title: 'Points de livraison', notes: deliveryPoint },
      { id: 'eco', title: 'Éco-acteurs', notes: eco },
    ].filter((section) => section.notes.length > 0);

    return (
      <div className={classes.hiddenWrapper}>
        <div ref={ref} className={classes.document}>
          {sections.map((section, sectionIndex) => (
            <section key={section.id} className={sectionIndex > 0 ? classes.sectionBreak : undefined}>
              {section.notes.map((note, noteIndex) => {
                const CardComponent = CARD_COMPONENT_BY_TYPE[note.type];
                return (
                  <div key={note.orders[0].id} className={classes.noteWrapper}>
                    {noteIndex === 0 && <h1>{formatSectionTitle(section)}</h1>}
                    <CardComponent note={note} isPrintMode />
                    <footer className={classes.pageFooter}>
                      <span>{formatSectionTitle(section)}</span>
                      <span>{`${noteIndex + 1}/${section.notes.length}`}</span>
                    </footer>
                  </div>
                );
              })}
            </section>
          ))}
        </div>
      </div>
    );
  }
);
